<template>
    <div class="position-relative component">
        <div class="install_buttons mt-3 mb-5 d-none">
            <basic-button
                :text="install.meal ?
                    trans(`general.page.space_forms.presets.install_meals`) :
                    trans(`general.page.space_forms.presets.meal_installed`)"
                :disabled="disabled.meal || !install.meal"
                :cls="['mb-2 btn-sm', install.meal ? 'btn-primary' : 'btn-success']"
                :icon="['fi', install.meal ? 'fi-rr-operation' : 'fi-rr-check']"
                :loading="installing.meal"
                @click="installPresets('meal')"/>

            <basic-button
                :text="install.meal_quantity ?
                    trans(`general.page.space_forms.presets.install_meals_quantities`) :
                    trans(`general.page.space_forms.presets.meal_quantity_installed`)"
                :disabled="disabled.meal_quantity || !install.meal_quantity"
                :cls="['mb-2 btn-sm', install.meal_quantity ? 'btn-primary' : 'btn-success']"
                :icon="['fi', install.meal_quantity ? 'fi-rr-operation' : 'fi-rr-check']"
                :loading="installing.meal_quantity"
                @click="installPresets('meal_quantity')"/>

            <basic-button
                :text="install.food ?
                    trans(`general.page.space_forms.presets.install_foods`) :
                    trans(`general.page.space_forms.presets.food_installed`)"
                :disabled="disabled.food || !install.food"
                :cls="['mb-2 btn-sm', install.food ? 'btn-primary' : 'btn-success']"
                :icon="['fi', install.food ? 'fi-rr-operation' : 'fi-rr-check']"
                :loading="installing.food"
                @click="installPresets('food')"/>
        </div>

        <div class="simplified-title text-center">
            <h1 v-html="trans(`general.page.space_forms.presets.plan_your_meals`)"></h1>
        </div>

        <div v-if="install.meal || install.meal_quantity" class="alert alert-info mt-3"
             v-html="trans(`general.page.space_forms.presets.plan_meals_preset_message`)">
        </div>
        <div v-else class="w-100">
            <p v-if="firstMenuPlannerCreated" v-html="trans('general.forms.settings.set_up_message')"></p>

            <div v-if="planner" class="calendar mt-3">
                <MenuForm
                    event-type="menu"
                    ref="menuForm"
                    :planner-id="planner.id"
                    v-on:event-saved="eventSaved($event)"
                    v-on:event-deleted="eventDeleted($event)"
                    @triggerModal="triggerModal"/>

                <div class="k-calendar">
                    <div :class="['day', {'today': today === date.date}]" v-for="(date, key) in calendarData" :key="key">
                        <div class="header">
                            <div class="day-name">
                                <small>{{ date.dayName }}</small>
                            </div>
                            <div class="date">
                                <h3>{{ date.dayOfMonth }}</h3>
                            </div>
                        </div>
                        <div class="items d-flex flex-column gap-1">
                            <div :class="['item px-3 d-flex justify-content-between align-items-center', {'bg-light-green': meal.description && meal.description !== ''}]"
                                 v-for="meal in formattedMeals[date.date]" :key="meal.id">
<!--                                @click="addEditPlanMeal(meal)"-->
                                <div class="w-100">
                                    {{ meal.title }}
                                </div>

                                <k-select
                                    :ref="`foodSelect_${meal.id}`"
                                    :label="trans('general.component.food_select.label')"
                                    :placeholder="trans('general.component.food_select.label')"
                                    :multiple="true"
                                    :show-tags="1"
                                    :allow-search="true"
                                    auto-close="false"
                                    :server-side-search="true"
                                    v-on:search="loadFoodOptions"
                                    v-on:search-enter="addNewOption($event, meal.id)"
                                    v-model="selectedFoods[meal.id]"
                                    :options="foodOptions">
                                    <template #noResults="{ search }">
                                        <div v-if="search !== ''"
                                             @click="addNewOption(search, meal.id)"
                                             role="button"
                                             class="d-flex justify-content-between border-radius bg-gray-100 p-1 ps-3 align-items-center">
                                            <label class="large">{{ search }}</label>
                                            <span class="small btn btn-success btn-sm">
                                                {{ trans('general.defaults.add')}}
                                                <i class="fi fi-rr-arrow-turn-down-left"></i>
                                            </span>
                                        </div>
                                        <div v-else>
                                            {{ trans('general.defaults.no_results_found') }}
                                        </div>
                                    </template>
                                </k-select>
                            </div>
<!--                            <div class="item new" @click="addPlanMeal(date.date)">-->
<!--                                <i class="fi fi-rr-plus"></i>-->
<!--                            </div>-->
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <setup-footer ref="setupFooter" :step="step" v-on="$listeners" @nextStep="nextStep" :next-disabled="nextDisabled"/>
    </div>
</template>

<script>
import BasicButton from "../../../theme/atoms/BasicButton.vue";
import VueTagsInput from "@johmun/vue-tags-input";
import axios from "axios";
import SetupFooter from "../molecules/SetupFooter.vue";
import moment from "moment";
import PlannerFormModal from "../../../modules/Planners/molecules/PlannerFormModal.vue";
import MenuForm from "../../../modules/Planners/molecules/MenuForm.vue";
import KSelect from "../../../theme/molecules/KSelect.vue";

export default {
    name: "PlanMeal",
    props: {
        props: Object,
        step: {
            type: Number
        }
    },
    components: {
        KSelect,
        MenuForm,
        SetupFooter,
        BasicButton,
        VueTagsInput,
        PlannerFormModal
    },
    watch: {
        'configurationRequired': {
            handler(val) {
                if (val === true) {
                    this.createCalendar();
                }
            },
            deep: true
        },
        addedMeals: {
            handler(newValue) {
                const tempFormattedMeals = {};
                newValue.forEach((item) => {
                    let date = moment(item.start).format('YYYY-MM-DD');

                    if (!tempFormattedMeals[date]) {
                        tempFormattedMeals[date] = [];
                    }

                    tempFormattedMeals[date].push(item);

                    const foods = item.description?.split('|');
                    if(foods) {
                        this.selectedFoods[item.id] = foods.map(a => {
                            return {
                                label: a,
                                id: item.id + '#' + a
                            };
                        });
                    }
                });
                this.formattedMeals = tempFormattedMeals;


            },
            deep: true
        },
    },
    computed: {
        configurationRequired() {
            return this.planner && this.install.meal === false && this.install.meal_quantity === false;
        },
    },
    data() {
        return {
            nextDisabled: true,
            eventType: 'menu',
            space: null,
            today: moment().format('YYYY-MM-DD'),
            planner: null,
            lastDate: null,
            firstMenuPlannerCreated: false,
            disabled: {
                meal: true,
                meal_quantity: true,
                food: true,
            },
            installing: {
                meal: false,
                meal_quantity: false,
                food: false,
            },
            install: {
                meal: true,
                meal_quantity: true,
                food: true,
            },
            presets: {
                meal: {},
                meal_quantity: {},
                food: {},
            },
            calendarData: {},
            addingMealsToCalendar: false,
            addedMeals: {},
            formattedMeals: {},
            selectedFoods: [],
            foodOptions: [],
        }
    },
    methods: {
        configure() {
            if (!this.props) {
                return null;
            }

            Object.entries(this.props).forEach(([key, value]) => {
                this[key] = value
            });

            this.space = JSON.parse(this.space);

            this.checkInstalledPresets();
        },
        checkInstalledPresets() {
            const requests = Object.keys(this.installing).map(type => {
                const _type = type.replace('_', '-');
                return axios.get(`/settings/${_type}`).then(response => {
                    const data = response.data.data;
                    if (data.total > 0) {
                        this.install[type] = false;
                        this.presets[type] = data.data;
                    }
                    this.disabled[type] = false;
                }).catch(error => {
                    this.$notify({type: 'error', text: error.toString()});
                    this.disabled[type] = false;
                });
            });

            Promise.all(requests).then(() => {
                const installPromises = Object.entries(this.install).map(([type, install]) => {
                    if (install) {
                        return this.installPresets(type);
                    } else {
                        return Promise.resolve();
                    }
                });

                Promise.all(installPromises).then(() => {
                    this.getOrCreateFirstMenuPlanner();
                });
            });
        },
        async installPresets(type) {
            this.installing[type] = true;
            let translate = (type === 'food' || type === 'meal');

            try {
                const response = await axios.post(`/settings/install/presets/${type}/${translate}`);
                const data = response.data;

                if (data.status === 'success') {
                    this.install[type] = false;
                    this.presets[type] = data.data;
                    this.$notify({
                        type: 'success',
                        text: trans(`general.page.space_forms.presets.${type}_installed`)
                    });

                } else {
                    this.$notify({type: data.status, text: data.message});
                }

            } catch (error) {
                this.$notify({type: 'error', text: error.toString()});

            } finally {
                this.installing[type] = false;
            }
        },
        getOrCreateFirstMenuPlanner() {
            if (this.install.meal === false && this.install.meal_quantity === false) {

                axios.post(`/planners/create-first/menu`).then(response => {
                    if (response.data.created) {
                        this.firstMenuPlannerCreated = true;
                    }

                    this.planner = response.data.planner;

                    this.getPlannerMeals();
                })
                .catch(error => {
                    this.$notify({type: 'error', text: error.toString()});
                });
            }
        },
        getNextWeekdaysWithNames(nextDays, startDate) {
            if (!this.space) {
                this.$notify({type: 'error', text: 'Space data could not be loaded!'});
                return;
            }

            let weekdays = {};
            let date = moment(startDate, 'YYYY-MM-DD');
            let workingDaysOfWeek = this.space.working_days.map(day => parseInt(day));

            let count = 0;
            while (count < nextDays) {
                let dayOfWeek = date.day();

                if (workingDaysOfWeek.includes(dayOfWeek)) {
                    let dayOfMonth = date.date();
                    let month = date.month();
                    let uniqueKey = `${dayOfMonth}-${month}`;
                    let _date = date.format('YYYY-MM-DD');
                    this.lastDate = _date;

                    weekdays[uniqueKey] = {
                        date: _date,
                        dayOfMonth: dayOfMonth,
                        dayName: date.format('dddd')
                    };
                    count++;
                }
                date.add(1, 'days');
            }

            this.loadFoodOptions();

            return weekdays;
        },
        createCalendar() {
            this.calendarData = this.getNextWeekdaysWithNames(1, this.planner.published_at);
        },
        addMealsToCalendar() {
            let dates = Object.values(this.calendarData).map(value => value.date);

            let form = new FormData();
            form.append('type', this.eventType);
            form.append('planner_id', this.planner.id);
            form.append('meals', JSON.stringify(this.presets.meal));
            form.append('dates', dates);

            axios.post(`/plans/bulk-create-for-dates`, form)
                .then(response => {
                    this.addedMeals = response.data.plans
                    this.$emit('stepDone');
                    this.$refs.setupFooter.showNextStep = true;
                })
                .catch(error => {
                    if (error.response.data.message === '' && error.response.data.errors) {
                        let errors = Object.values(error.response.data.errors);
                        this.errors = errors.map(val => val[0] || 0)
                    } else {
                        this.$notify({type: 'error', text: error.response.data.message});
                    }
                })
        },
        getPlannerMeals() {
            setTimeout(() => {
                let dates = this.calendarData;
                let firstItem = this.calendarData[Object.keys(dates)[0]];

                axios.get(`/plans/planner/${this.planner.id}`, {
                    params: {
                        start: firstItem.date,
                        end: this.lastDate
                    }
                })
                    .then(response => {
                        if(response.data.length > 0) {
                            this.addedMeals = response.data;
                            this.$emit('stepDone');
                            this.$refs.setupFooter.showNextStep = true;
                        } else {
                            this.addMealsToCalendar();
                        }
                    })
                    .catch(error => {
                        if (error.response.data.message === '' && error.response.data.errors) {
                            let errors = Object.values(error.response.data.errors);
                            this.errors = errors.map(val => val[0] || 0)
                        } else {
                            this.$notify({type: 'error', text: error.response.data.message});
                        }
                    }).finally(() => {
                        this.nextDisabled = false;
                    });
            }, 1000);
        },
        addEditPlanMeal(model) {
            this.triggerModal(true, model);
        },
        triggerModal(trigger, model = {}) {
            if(typeof trigger === 'object') {
                model = trigger.model;
                trigger = trigger.trigger;
            }

            if(trigger) {
                this.$refs.menuForm.open(model);
            } else {
                this.$refs.menuForm.close(model);
            }
        },
        eventSaved(event) {
            this.$wait.end('submit-plan');
            this.triggerModal(false);
            this.getPlannerMeals();
        },
        eventDeleted(event) {
            this.triggerModal(false);
            this.getPlannerMeals();
        },
        setFooterBtnText(state) {
            this.$refs.setupFooter.setButtonText(state);
        },
        loadFoodOptions(search = '') {
            axios.get(`/settings/food?search=${search}&search_fields=label:like&limit=6`).then(response => {
                this.foodOptions = response.data.data.data.map(a => {
                    return {label: a.label, id: a.label};
                });
            });
        },
        addNewOption(option, key) {
            if(this.selectedFoods[key] && this.selectedFoods[key].find(a => a.label === option)) return;
            const newOption = {label: option, id: option};
            !this.selectedFoods[key] ? this.selectedFoods[key] = [newOption] : this.selectedFoods[key].push(newOption);
            this.foodOptions.push(newOption);
            const foodSelectRef = this.$refs[`foodSelect_${key}`][0];
            if (foodSelectRef) {foodSelectRef.setSearchQuery('')}
        },

        editPlan() {
            this.$refs.plannerModal.open(this.planner);
        },
        editPlanSaved() {
            this.getOrCreateFirstMenuPlanner();
            this.$refs.plannerModal.close();
        },
        addPlanMeal(date) {
            this.addEditPlanMeal({origin: date});
        },

        nextStep() {
            const mealFoods = Object.keys(this.selectedFoods).reduce((acc, mealId) => {
                acc[mealId] = this.selectedFoods[mealId].map(a => a.label).join('|');
                return acc;
            }, {});

            const data = {
                type: 'menu',
                plans: mealFoods,
            };

            let form = new FormData();
            form.append('data', JSON.stringify(data));
            axios.post(`/plans/update-multiple`, form);
        }
    },
    mounted() {
        this.configure();
    }
}
</script>
